import { useApiMutation } from "@gladis/api-client";
import { useState, type ReactNode } from "react";
import { Button } from "@gladis/ui";
import { errorSentence, useT } from "@gladis/ui/i18n";

interface OwnedHousehold {
  id: string;
  name: string;
}

/**
 * Account deletion (FR-031, FR-032).
 *
 * Two-step by design. Deletion is irreversible and destroys memberships, so a single click is the
 * wrong affordance — the confirmation exists to make the action deliberate rather than to add
 * ceremony.
 */
export function DeleteAccountDialog({ onDeleted }: { onDeleted: () => void }): ReactNode {
  const t = useT();
  const [confirming, setConfirming] = useState(false);

  const remove = useApiMutation<void, void>((client) => client.delete("/me"), {
    onSuccess: onDeleted,
  });

  // The API names the households that block deletion, so the refusal can say what to do about it
  // rather than only that it was refused.
  const blocked =
    remove.error?.is("IDENT_OWNER_CANNOT_DELETE") === true
      ? (remove.error.details?.["ownedHouseholds"] as OwnedHousehold[] | undefined)
      : undefined;

  return (
    <section className="mt-10 rounded-control border border-line bg-panel p-5">
      <h2 className="text-xl font-semibold">{t("identity.deleteAccount.heading")}</h2>

      {blocked ? (
        <div className="mt-4 text-muted" role="alert">
          <p>
            You own {blocked.length === 1 ? "a household" : "households"} that would be left without
            an owner:
          </p>
          <ul className="mt-2 list-inside list-disc">
            {blocked.map((household) => (
              <li key={household.id}>{household.name}</li>
            ))}
          </ul>
          <p className="mt-2">
            Transfer ownership or delete {blocked.length === 1 ? "it" : "them"} first.
          </p>
        </div>
      ) : null}

      {remove.isError && !blocked ? (
        <p className="mt-4 text-sm text-muted" role="alert">
          {errorSentence(remove.error)}
        </p>
      ) : null}

      {confirming ? (
        <div className="mt-4">
          <p className="text-muted">
            This deletes your account and removes you from every household. It cannot be undone.
            {/* What survives is worth saying: people reasonably fear that deleting themselves will
                blank out shared history. */}
          </p>
          <p className="mt-2 text-muted">{t("identity.deleteAccount.consequence")}</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Button type="button" onClick={() => remove.mutate()} disabled={remove.isPending}>
              {remove.isPending ? t("common.deleting") : t("identity.deleteAccount.confirm")}
            </Button>
            <Button type="button" onClick={() => setConfirming(false)}>
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      ) : (
        <Button className="mt-5" type="button" onClick={() => setConfirming(true)}>
          {t("identity.deleteAccount.button")}
        </Button>
      )}
    </section>
  );
}
